import { useBunja } from 'bunja/react';
import { addMinutes } from 'date-fns';
import { useAtomValue } from 'jotai';

import { CompetitionStatusEnum } from '@flipster/apis/v2/trading-competition/types';
import { FormattedMessage } from '@flipster/i18n';
import { IconButton } from '@flipster/react/design-system/icon-button';
import { Tooltip } from '@flipster/react/design-system/tooltip/index';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import { css } from '@flipster/styled-system/css';
import { UsdtCurrency } from '@flipster/utils/constants/currency';
import { timestampToDate } from '@flipster/utils/datetime';

import { DesktopTable, TableHeaderItemProps } from '../../../component/table';
import { competitionBunja, competitionTabsBunja } from '../../../state';
import { competitionRankingsBunja } from '../state';
import { MobileList } from './mobile-list';
import { Pagination } from './pagination';
import { TableRow } from './table-row';

export const RankList = () => {
  const { competitionAtom } = useBunja(competitionBunja);
  const { rankingsAtom } = useBunja(competitionRankingsBunja);
  const { selectedEventTabValueAtom } = useBunja(competitionTabsBunja);
  const rankings = useAtomValue(rankingsAtom);
  const selectedEventTabValue = useAtomValue(selectedEventTabValueAtom);
  const competition = useAtomValue(competitionAtom);

  const isVolume = selectedEventTabValue === 'volume';
  const isRoi = selectedEventTabValue === 'roi';

  const headerItems: Array<TableHeaderItemProps> = [
    { title: <FormattedMessage id='Common.r251201.Trader' />, align: 'left' },
    {
      title: <FormattedMessage id='Competition.r251215.Expected_reward_symbol' values={{ symbol: UsdtCurrency }} />,
      align: 'right',
      icon: (
        <Tooltip.Root>
          <Tooltip.Trigger>
            <IconButton hierarchy='quaternary' size='small' icon={<InformationCircleIcon />} />
          </Tooltip.Trigger>
          <Tooltip.Content bottomSheetTitle={<FormattedMessage id='Competition.r251217.Expected_reward' />} portal>
            <FormattedMessage id='Competition.r251215.Expected_reward_symbol_desc' />
          </Tooltip.Content>
        </Tooltip.Root>
      ),
    },
    {
      title: isVolume ? (
        <FormattedMessage id='Competition.r251215.Volume_symbol' values={{ symbol: UsdtCurrency }} />
      ) : isRoi ? (
        <FormattedMessage id='Tmp.inseok.Roi_symbol' values={{ symbol: '%' }} />
      ) : (
        <FormattedMessage id='Competition.r251218.Pnl_symbol' values={{ symbol: UsdtCurrency }} />
      ),
      align: 'right',
      icon: isVolume ? (
        <Tooltip.Root>
          <Tooltip.Trigger>
            <IconButton hierarchy='quaternary' size='small' icon={<InformationCircleIcon />} />
          </Tooltip.Trigger>
          <Tooltip.Content bottomSheetTitle={<FormattedMessage id='Common.r250923.Volume' />} portal>
            <FormattedMessage id='Competition.r251215.Volume_symbol_desc' />
          </Tooltip.Content>
        </Tooltip.Root>
      ) : undefined,
    },
  ];

  const NoItem = () => {
    // competition 시작 후 첫 10분간은 랭킹 집계중인 상태
    const nowUtc = new Date(Date.now());
    const isCollecting =
      competition?.competitionStatus === CompetitionStatusEnum.Ongoing &&
      !!competition?.competitionStartTime &&
      nowUtc.getTime() <= addMinutes(timestampToDate(competition.competitionStartTime), 10).getTime();

    if (isCollecting)
      return (
        <p
          className={css({
            textStyle: 'Title/S',
            textAlign: 'center',
          })}
        >
          <FormattedMessage id='Competition.r251218.Hang_on_leaderboard_collecting_results' />
        </p>
      );

    // FIXME:아이콘 추가될 예정
    return (
      <p
        className={css({
          textStyle: 'Title/S',
          textAlign: 'center',
        })}
      >
        <FormattedMessage id='Competition.r251218.No_traders_ranked_yet_trade_now_first_one' />
      </p>
    );
  };

  return (
    <section className={css({ py: 24 })}>
      <DesktopTable
        id={`leaderboard-table-${selectedEventTabValue}`}
        noItem={<NoItem />}
        headerItems={headerItems}
        items={rankings}
        Row={TableRow}
      />
      <MobileList />
      <Pagination />
    </section>
  );
};
